import { ActivityResponderDialog } from "@/components/conversations/activities/activity-responder-dialog";
import { useResponderSend } from "@/components/conversations/activities/use-responder-send";
import { useActivity } from "@/lib/api/activities";
import { useConnectLibrary } from "@/lib/api/connect-library";
import { colorsV2 } from "@somethings-ui/core";
import { Rocket } from "lucide-react";
import { useMemo, useState } from "react";
import { ConnectResponderRow } from "./connect-responder-row";
import { MicSelector } from "./mic-selector";
import type { ConfirmedResponderAnswer } from "./use-connect-responder-row";

interface ConnectMomentResponderProps {
  isOpen: boolean;
  entityId: string;
  teenName: string;
  onClose: () => void;
}

export function ConnectMomentResponder({
  isOpen,
  entityId,
  teenName,
  onClose,
}: ConnectMomentResponderProps) {
  const { data: activity } = useActivity(entityId);
  const { data: libraryData } = useConnectLibrary();

  const [selectedMicId, setSelectedMicId] = useState<string | null>(null);
  const [confirmedByIndex, setConfirmedByIndex] = useState<
    Record<number, ConfirmedResponderAnswer | null>
  >({});
  const [recordingByIndex, setRecordingByIndex] = useState<
    Record<number, boolean>
  >({});

  const questionIds = useMemo(
    () => (activity?.config as { question_ids?: string[] })?.question_ids ?? [],
    [activity],
  );

  const libraryByQuestionId = useMemo(() => {
    const map = new Map(
      (libraryData?.library ?? []).map((row) => [row.questionId, row]),
    );
    return map;
  }, [libraryData]);

  const isAnyRowRecording = Object.values(recordingByIndex).some(Boolean);

  const rowCallbacks = useMemo(
    () =>
      [0, 1, 2].map((index) => ({
        onRecordingStateChange: (isRecording: boolean) =>
          setRecordingByIndex((prev) => ({ ...prev, [index]: isRecording })),
        onConfirmedChange: (confirmed: ConfirmedResponderAnswer | null) =>
          setConfirmedByIndex((prev) => ({ ...prev, [index]: confirmed })),
      })),
    [],
  );

  const canSend =
    questionIds.length === 3 &&
    questionIds.every((_, i) => !!confirmedByIndex[i]) &&
    !isAnyRowRecording;

  const { isSending, handleSend } = useResponderSend({
    entityId,
    activityType: "connect_moment",
    isOpen,
    canSend,
    buildResult: () => ({
      responder_answers: questionIds.map((questionId, i) => ({
        question_id: questionId,
        audio_s3_key: confirmedByIndex[i]!.audioS3Key,
        duration_ms: confirmedByIndex[i]!.durationMs,
      })),
    }),
    buildExtraEventProps: () => ({
      prepopulated_answer_count: Object.values(confirmedByIndex).filter(
        (answer) => answer?.fromLibrary,
      ).length,
    }),
    onClose,
  });

  return (
    <ActivityResponderDialog
      isOpen={isOpen}
      onClose={onClose}
      icon={
        <Rocket
          className="h-6 w-6 shrink-0"
          style={{ color: colorsV2.text.primary }}
        />
      }
      title="Connection cards"
      subtitle={`${teenName} sent you connection cards. Answer each question to reveal their answers!`}
      contentClassName="w-[748px] max-w-[748px] sm:max-w-[748px]"
      bodyClassName="px-6 pb-2 pt-3"
      canSend={canSend}
      isSending={isSending}
      onSend={handleSend}
    >
      <div className="mb-2 flex items-center justify-end gap-1">
        <MicSelector
          selectedDeviceId={selectedMicId}
          onSelect={setSelectedMicId}
          disabled={isAnyRowRecording}
        />
      </div>
      <div className="flex flex-col gap-2">
        {questionIds.map((questionId, i) => {
          const index = i as 0 | 1 | 2;
          return (
            <ConnectResponderRow
              key={questionId}
              entityId={entityId}
              index={index}
              questionId={questionId}
              libraryAnswer={libraryByQuestionId.get(questionId) ?? null}
              isAnotherRowRecording={
                isAnyRowRecording && !recordingByIndex[index]
              }
              micDeviceId={selectedMicId}
              onRecordingStateChange={
                rowCallbacks[index].onRecordingStateChange
              }
              onConfirmedChange={rowCallbacks[index].onConfirmedChange}
            />
          );
        })}
      </div>
    </ActivityResponderDialog>
  );
}
